import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { handleError } from "../../../../utils/ErrorHandler";

const initialState = {
  list: [],
  game: null,
  error: "",
  isFetchingAll: false,    // For getAllGames
  isFetchingSingle: false, // For getGameById
  isSubmitting: false,     // For createGame and updateGame
  isDeleting: false,       // For deleteGame
};

// Get all games (paginated)
export const getAllGames = createAsyncThunk(
  "game/getAllGames",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`product?query=all`);
      return data;
    } catch (error) {
      const errorMessage = handleError(error, "Failed to fetch all games");
      return rejectWithValue(errorMessage);
    }
  }
);

// Get game by ID
export const getGameById = createAsyncThunk(
  "game/getGameById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`product/${id}`);
      return data;
    } catch (error) {
      const errorMessage = handleError(error, "Failed to fetch game details");
      return rejectWithValue(errorMessage);
    }
  }
);

// Create game
export const createGame = createAsyncThunk(
  "game/createGame",
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`product/`, values, {
        headers: {
          "Content-Type": "multipart/form-data", // Override default
        },
      });
      toast.success("Game added successfully!");
      return data;
    } catch (error) {
      const errorMessage = handleError(error, "Failed to add game");
      return rejectWithValue(errorMessage);
    }
  }
);

// Update game
export const updateGame = createAsyncThunk(
  "game/updateGame",
  async ({ id, values }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`product/${id}`, values, {
        headers: {
          "Content-Type": "multipart/form-data", // Override default
        },
      });
      toast.success("Game updated successfully!");
      return data;
    } catch (error) {
      const errorMessage = handleError(error, "Failed to update game");
      return rejectWithValue(errorMessage);
    }
  }
);

// Delete game
export const deleteGame = createAsyncThunk(
  "game/deleteGame",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(`product/${id}`);
      dispatch(getAllGames()); // Refresh the list after deletion
      toast.success("Game deleted successfully!");
      return id; // Return the deleted ID for filtering
    } catch (error) {
      const errorMessage = handleError(error, "Failed to delete game");
      return rejectWithValue(errorMessage);
    }
  }
);

const gameSlice = createSlice({
  name: "game",
  initialState,
  reducers: {
    clearGame: (state) => {
      state.game = null;
    },
  },
  extraReducers: (builder) => {
    // getAllGames
    builder.addCase(getAllGames.pending, (state) => {
      state.isFetchingAll = true;
      state.error = "";
    });
    builder.addCase(getAllGames.fulfilled, (state, action) => {
      state.isFetchingAll = false;
      state.list = action.payload;
    });
    builder.addCase(getAllGames.rejected, (state, action) => {
      state.isFetchingAll = false;
      state.error = action.payload;
    });

    // getGameById
    builder.addCase(getGameById.pending, (state) => {
      state.isFetchingSingle = true;
      state.error = "";
    });
    builder.addCase(getGameById.fulfilled, (state, action) => {
      state.isFetchingSingle = false;
      state.game = action.payload;
    });
    builder.addCase(getGameById.rejected, (state, action) => {
      state.isFetchingSingle = false;
      state.error = action.payload;
    });

    // CreateGame
    builder.addCase(createGame.pending, (state) => {
      state.isSubmitting = true;
      state.error = "";
    });
    builder.addCase(createGame.fulfilled, (state, action) => {
      state.isSubmitting = false;
      state.list.push(action.payload);
    });
    builder.addCase(createGame.rejected, (state, action) => {
      state.isSubmitting = false;
      state.error = action.payload;
    });

    // updateGame
    builder.addCase(updateGame.pending, (state) => {
      state.isSubmitting = true;
      state.error = "";
    });
    builder.addCase(updateGame.fulfilled, (state, action) => {
      state.isSubmitting = false;
      const updatedGame = action.payload;
      state.list = state.list.map((game) =>
        game.id === updatedGame.id ? updatedGame : game
      );
      state.game = updatedGame;
    });
    builder.addCase(updateGame.rejected, (state, action) => {
      state.isSubmitting = false;
      state.error = action.payload;
    });

    // deleteGame
    builder.addCase(deleteGame.pending, (state) => {
      state.isDeleting = true;
      state.error = "";
    });
    builder.addCase(deleteGame.fulfilled, (state, action) => {
      state.isDeleting = false;
      state.list = state.list.filter((game) => game.id !== action.payload);
    });
    builder.addCase(deleteGame.rejected, (state, action) => {
      state.isDeleting = false;
      state.error = action.payload;
    });
  },
});

export default gameSlice.reducer;
export const { clearGame } = gameSlice.actions;